import { RISK } from "@/lib/skills/constants";
import type { Category, RiskLevel, SkillListItem } from "@/lib/skills/types";

type SkillSort = "best" | "downloads" | "rating" | "updated" | "safest";

interface CatalogFilters {
  search?: string;
  categories?: Category[];
  risks?: RiskLevel[];
  minRating?: number;
  freshnessDays?: number;
  authors?: string[];
}

export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

export function filterSkills(
  skills: SkillListItem[],
  filters: CatalogFilters,
  now: Date = new Date(),
): SkillListItem[] {
  return skills.filter((s) => {
    if (filters.search && !s.title.toLowerCase().includes(filters.search.toLowerCase()))
      return false;
    if (filters.categories?.length && !filters.categories.some((c) => s.cat.includes(c)))
      return false;
    if (filters.risks?.length && !filters.risks.includes(s.risk)) return false;
    if (typeof filters.minRating === "number" && s.rating < filters.minRating) return false;
    if (
      typeof filters.freshnessDays === "number" &&
      daysSince(s.updatedAt, now) > filters.freshnessDays
    ) {
      return false;
    }
    if (filters.authors?.length && !filters.authors.includes(s.authorId)) return false;
    return true;
  });
}

export function sortSkills(skills: SkillListItem[], sort: SkillSort): SkillListItem[] {
  const copy = [...skills];
  copy.sort((a, b) => {
    if (sort === "downloads") return b.downloads - a.downloads;
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "updated")
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    if (sort === "safest") return RISK[a.risk].rank - RISK[b.risk].rank;
    // best: rating with a vetted bonus
    const score = (s: SkillListItem) => s.rating + (RISK[s.risk].rank === 0 ? 0.5 : 0);
    return score(b) - score(a);
  });
  return copy;
}

export interface FacetCounts {
  risk: Record<RiskLevel, number>;
  category: Record<Category, number>;
  authors: { id: string; name: string; count: number }[];
}

export function facetCounts(skills: SkillListItem[]): FacetCounts {
  const risk: Record<RiskLevel, number> = { vetted: 0, low: 0, review: 0, high: 0 };
  const category: Record<Category, number> = { "UI/UX": 0, Security: 0, Efficiency: 0 };
  const authorMap = new Map<string, { id: string; name: string; count: number }>();
  for (const s of skills) {
    risk[s.risk] += 1;
    for (const c of s.cat) category[c] += 1;
    const existing = authorMap.get(s.authorId);
    if (existing) existing.count += 1;
    else authorMap.set(s.authorId, { id: s.authorId, name: s.authorName, count: 1 });
  }
  const authors = [...authorMap.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );
  return { risk, category, authors };
}
